import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { CalendarClock, RefreshCw } from "lucide-react";
import { Pagination } from "@/components/Pagination";
import { usePaged } from "@/lib/usePaged";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso || "—"; }
};
const STATUS = {
  completed: { l: "Tamamlandı", c: "bg-emerald-100 text-emerald-700" },
  deferred: { l: "Ertelendi", c: "bg-amber-100 text-amber-700" },
  running: { l: "Çalışıyor", c: "bg-sky-100 text-sky-700" },
};
const badge = (r) => {
  if (!r) return { l: "—", c: "bg-slate-100 text-slate-400" };
  if (r.skipped) return { l: "Atlandı", c: "bg-slate-100 text-slate-500" };
  return STATUS[r.status] || { l: r.status || "kaydedildi", c: "bg-slate-100 text-slate-600" };
};
const HEALTH = {
  ok: { dot: "bg-emerald-500", label: "Zamanında", cls: "text-emerald-600" },
  late: { dot: "bg-red-500", label: "Gecikti", cls: "text-red-600" },
  never: { dot: "bg-slate-300", label: "Henüz çalışmadı", cls: "text-slate-400" },
};

export default function ScheduledTasks() {
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.get("/admin/cron-status").then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const lateCount = (data?.crons || []).filter((c) => c.health === "late").length;
  const recent = data?.recent || [];
  const recentPaged = usePaged(recent, 20);

  return (
    <div className="space-y-6" data-testid="scheduled-tasks-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-slate-900"><CalendarClock className="h-7 w-7 text-brand-green" /> Zamanlanmış Görevler</h1>
          <p className="mt-1 text-sm text-slate-500">Platform tarafından otomatik çalışan <b>5 zamanlanmış görev</b> (cron) ve son çalışma zamanları. Bazı işler (döviz/akaryakıt, doğum günü SMS, Ayın Personeli, belge saklama) bu görevlere bağlı olarak çalışır.</p>
        </div>
        <div className="flex items-center gap-2">
          {lateCount > 0 && <span data-testid="cron-late-badge" className="rounded-full bg-red-100 px-3 py-1.5 text-xs font-bold text-red-700">{lateCount} görev gecikti</span>}
          <button data-testid="cron-refresh" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"><RefreshCw className="h-4 w-4" /> Yenile</button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="cron-cards">
        {(data?.crons || []).map((c) => {
          const b = badge(c.last_run);
          const h = HEALTH[c.health] || HEALTH.never;
          return (
            <div key={c.name} data-testid={`cron-${c.name}`} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <p className="flex items-center gap-2 font-mono text-sm font-bold text-slate-900">
                  <span data-testid={`cron-health-${c.name}`} title={h.label} className={`h-2.5 w-2.5 shrink-0 rounded-full ${h.dot}`} />
                  {c.name}
                </p>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${b.c}`}>{b.l}</span>
              </div>
              <p className="mt-1 text-xs font-semibold text-brand-green">{c.schedule}</p>
              <p className="mt-1 flex-1 text-[11px] leading-snug text-slate-500">{c.desc}</p>
              <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2 text-[11px]">
                <span className="text-slate-400">Son çalışma: <span className="font-semibold text-slate-600">{c.last_run ? fmt(c.last_run.started_at || c.last_run.at) : "henüz çalışmadı"}</span></span>
                <span className={`font-semibold ${h.cls}`}>{h.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-4 text-[11px] text-slate-400">
        <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Zamanında çalıştı</span>
        <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-red-500" /> Gecikti (yöneticilere uyarı gönderilir)</span>
        <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-slate-300" /> Henüz çalışmadı</span>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="font-heading text-base font-bold text-slate-900">Son Etkinlik</p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr>
              <th className="px-3 py-2">Zaman</th><th className="px-3 py-2">Görev</th><th className="px-3 py-2">Durum</th><th className="px-3 py-2">Detay</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100" data-testid="cron-recent">
              {recent.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400">Kayıt yok.</td></tr>
              ) : recentPaged.slice.map((r) => {
                const b = badge(r);
                const detail = [
                  r.processed != null ? `${r.processed} işlendi` : null,
                  r.applied != null ? `uygulanan ${r.applied}` : null,
                  r.reverted != null ? `geri alınan ${r.reverted}` : null,
                  r.reason || null,
                  r.dry ? "kuru test" : null,
                ].filter(Boolean).join(" · ");
                return (
                  <tr key={r.id} data-testid={`cron-recent-row-${r.id}`}>
                    <td className="whitespace-nowrap px-3 py-2 text-slate-500">{fmt(r.started_at || r.at || r.finished_at)}</td>
                    <td className="px-3 py-2 font-mono text-[12px] text-slate-700">{r.job}</td>
                    <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${b.c}`}>{b.l}</span></td>
                    <td className="px-3 py-2 text-[11px] text-slate-400">{detail || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {recent.length > 0 && (
            <div className="mt-2"><Pagination page={recentPaged.page} pageSize={recentPaged.pageSize} total={recentPaged.total}
              onPage={recentPaged.setPage} onPageSize={recentPaged.setPageSize} testid="cron-recent-pagination" /></div>
          )}
        </div>
      </div>
    </div>
  );
}
